// @flow
import React, { PureComponent } from 'react';
import { View, StyleSheet, Text } from 'react-native';
import { Field } from 'redux-form';
import { ProgressBar } from 'trpc-react-native-ui';

import PasswordService from '../../../lib/password';
import { HoshiInput, FullButton } from '../../common';
import theme from '../../../theme-config';
import I18n from '../../../lib/i18n';
import EyeIcon from './components/EyeIcon';

class ChangePasswordForm extends PureComponent<Props, State> {
  passwordInput: any;
  state = {
    passwordStrengthIndicators: PasswordService.strengthIndicators(''),
    isNewPasswordVisible: false,
    isOldPasswordVisible: false,
  };

  styles = getStyles();

  handlePasswordChange = (password: string) =>
    this.setState({ passwordStrengthIndicators: PasswordService.strengthIndicators(password) });

  isPasswordStrengthIndicatorsEmpty = (indicatorsList: Object) => {
    if (indicatorsList && Object.keys(indicatorsList).every(indicator => indicatorsList[indicator] === null)) {
      return true;
    }
    return false;
  };

  toggleStateBoolean = (stateBooleanName: 'isNewPasswordVisible' | 'isOldPasswordVisible') => {
    this.setState(state => {
      const newState: { isNewPasswordVisible?: boolean, isOldPasswordVisible?: boolean } = {};
      newState[stateBooleanName] = !state[stateBooleanName];
      return newState;
    });
  };
  toggleNewPasswordVisible = () => this.toggleStateBoolean('isNewPasswordVisible');
  toggleOldPasswordVisible = () => this.toggleStateBoolean('isOldPasswordVisible');

  renderPasswordStrengthIndicator = () => {
    const indicators = this.state.passwordStrengthIndicators;
    if (!indicators || this.isPasswordStrengthIndicatorsEmpty(indicators)) return null;
    return (
      <View>
        <ProgressBar progress={indicators.progress} fillColor={indicators.color} />
        <View style={this.styles.suggestionsContainer}>
          {indicators.suggestions.map(suggestion => (
            <Text key={suggestion} style={this.styles.passwordSuggestion}>
              • {suggestion}
            </Text>
          ))}
        </View>
      </View>
    );
  };

  render() {
    const newPasswordInputType = this.state.isNewPasswordVisible ? 'text' : 'password';
    const oldPasswordInputType = this.state.isOldPasswordVisible ? 'text' : 'password';

    const iconNewPassword = (
      <EyeIcon onPress={this.toggleNewPasswordVisible} isPasswordVisible={this.state.isNewPasswordVisible} />
    );
    const iconOldPassword = (
      <EyeIcon onPress={this.toggleOldPasswordVisible} isPasswordVisible={this.state.isOldPasswordVisible} />
    );

    return (
      <View>
        <Field
          name="oldPassword"
          component={HoshiInput}
          type={oldPasswordInputType}
          placeholder={I18n.t('oldPassword')}
          returnKeyType="next"
          style={this.styles.input}
          inputStyle={this.styles.textInput}
          icon={iconOldPassword}
          onSubmitEditing={() => this.passwordInput.getRenderedComponent().focus()}
        />
        <Field
          ref={ref => (this.passwordInput = ref)}
          name="newPassword"
          component={HoshiInput}
          type={newPasswordInputType}
          placeholder={I18n.t('newPassword')}
          forwardRef
          returnKeyType="go"
          onSubmitEditing={this.props.handleSubmit}
          style={this.styles.input}
          inputStyle={this.styles.textInput}
          icon={iconNewPassword}
          onChangeText={this.handlePasswordChange}
        />
        {this.renderPasswordStrengthIndicator()}
        <FullButton
          text={I18n.t('confirm')}
          onPress={this.props.handleSubmit}
          isLoading={this.props.isChangingPassword}
        />
      </View>
    );
  }
}

type Props = StateProps & DispatchProps;

export type StateProps = {
  isChangingPassword: boolean,
};

export type DispatchProps = {
  handleSubmit: Function,
};

type State = {
  passwordStrengthIndicators: Object,
  isNewPasswordVisible: boolean,
  isOldPasswordVisible: boolean,
};

const getStyles = () =>
  StyleSheet.create({
    input: {
      marginVertical: 8,
    },
    textInput: {
      height: 50,
    },
    passwordSuggestion: {
      marginTop: 5,
      color: theme.errorColor,
      fontSize: 12,
      backgroundColor: 'transparent',
      textAlign: 'left',
    },
    suggestionsContainer: {
      minHeight: 50,
    },
  });

export default ChangePasswordForm;
